import { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useSubmitFeedbackMutation } from '../features/feedback/mutations';

export interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MAX_LENGTH = 500;

export function FeedbackModal({
  open,
  onOpenChange,
}: FeedbackModalProps): JSX.Element {
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submitFeedback = useSubmitFeedbackMutation();

  function handleClose() {
    if (submitFeedback.isPending) return;
    setMessage('');
    setError(null);
    onOpenChange(false);
  }

  async function handleSubmit() {
    setError(null);
    const trimmed = message.trim();

    if (trimmed.length === 0 || trimmed.length > MAX_LENGTH) {
      setError('Please enter a message (1–500 characters).');
      return;
    }

    try {
      await submitFeedback.mutateAsync({ message: trimmed });
      toast.success('Thanks for your feedback!');
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="feedback-modal-title">
            Send Feedback
          </DialogTitle>
          <DialogDescription>
            Share a suggestion or report an issue. We read every submission.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="feedback-message">Your message</Label>
            <textarea
              id="feedback-message"
              data-testid="feedback-message"
              className="w-full rounded-lg border border-border bg-bg-1 px-3 py-2 text-body text-text-0 placeholder:text-text-2 focus:outline-none focus:ring-2 focus:ring-accent-primary resize-none"
              rows={4}
              maxLength={MAX_LENGTH}
              placeholder="What's on your mind?"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (error) setError(null);
              }}
              disabled={submitFeedback.isPending}
              aria-describedby="feedback-char-count"
            />
            <p
              id="feedback-char-count"
              className="text-small text-text-2 text-right"
              aria-label={`${message.length} of ${MAX_LENGTH} characters used`}
            >
              {message.length} / {MAX_LENGTH}
            </p>
          </div>

          {error && (
            <p
              className="text-small text-arcade-danger"
              role="alert"
              data-testid="feedback-error"
            >
              {error}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-3 pt-2">
          <Button
            data-testid="feedback-submit-button"
            size="lg"
            onClick={() => { void handleSubmit(); }}
            disabled={submitFeedback.isPending || message.trim().length === 0}
          >
            {submitFeedback.isPending ? 'Submitting\u2026' : 'Submit'}
          </Button>
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={submitFeedback.isPending}
            data-testid="feedback-cancel-button"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
